import React, { FC, useEffect, useMemo, useState, useContext } from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	fromDateTimeLocalValue,
	toDateTimeLocalValue,
} from "@/features/training/lib/trainingDateUtils";
import { CreateTrainingDrillSessionRequest } from "@/features/training/model/Training/TrainingDrillSession";
import { CustomCloseButton, CustomSaveButton } from "@/features/training/ui";
import CustomInput from "@/shared/forms/CustomInput";
import CustomSelect from "@/shared/forms/CustomSelect";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import { Form, Formik, useFormikContext } from "formik";

import { useMutateCreateDrillTrainingSession } from "@/service/TrainingMutations";
import {
	useFetchDrill,
	useFetchDrills,
	useFetchUserSummariesByCurrentCompany,
} from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(timezonePlugin);

/** Form values: Date is string (datetime-local), Description for display only */
type NewDrillSessionFormValues = Omit<
	CreateTrainingDrillSessionRequest,
	"Date"
> & {
	Date: string | Date;
	Description?: string;
};

interface NewDrillSessionProps {
	drillId?: number;
	open: boolean;
	handleClose: () => void;
	selectedDate?: unknown;
}

/** Utility: normalize various API shapes into an array */
function normalizeArray<T>(input: unknown): T[] {
	if (Array.isArray(input)) return input as T[];
	if (typeof input === "object" && input != null) {
		const values = input as Record<string, unknown>;
		const data = values["data"];
		if (Array.isArray(data)) return data as T[];
		const items = values["items"];
		if (Array.isArray(items)) return items as T[];
		return Object.values(values) as T[];
	}
	return [];
}

export const NewDrillSession: FC<NewDrillSessionProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="new-drill-session-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="new-drill-session-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.DrillTab.PanelRight.Drill.NewDrillSession.L124C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5, mb: 0.75 }}
				>
					<Translate String="Training.NewDrillSession.Title">
						Add Training Drill Session
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block" }}
				>
					<Translate String="Training.NewDrillSession.Subtitle">
						Schedule a new drill session and assign the responsible person.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelRight.Drill.NewDrillSession.L146C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				<NewDrillSessionForm {...props} />
			</DialogContent>
		</Dialog>
	);
};

export const NewDrillSessionForm: FC<NewDrillSessionProps> = (props) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { timezone } = useTime();
	// Local state for the currently selected drill (used to fetch the drill details)
	const [selectedDrillId, setSelectedDrillId] = useState<number | undefined>(
		props.drillId,
	);

	// Queries
	const { isLoading: isLoadingDrills, data: drillsRaw } = useFetchDrills();
	const drills = useMemo(
		() => normalizeArray<{ Id: number; Name: string }>(drillsRaw),
		[drillsRaw],
	);

	const { isLoading: isLoadingTrainerOptions, data: trainerOptionsRaw } =
		useFetchUserSummariesByCurrentCompany();
	const trainers = useMemo(
		() => normalizeArray<{ Id: number; Name: string }>(trainerOptionsRaw),
		[trainerOptionsRaw],
	);

	const { data: drill, isLoading: isLoadingDrill } =
		useFetchDrill(selectedDrillId);

	// Mutation (React Query style)
	const mutationOrFn = useMutateCreateDrillTrainingSession();

	const handleSubmit = async (values: NewDrillSessionFormValues) => {
		try {
			const request: CreateTrainingDrillSessionRequest = {
				DrillId: values.DrillId,
				Date: fromDateTimeLocalValue(
					typeof values.Date === "string"
						? values.Date
						: values.Date.toISOString(),
					timezone,
				),
				ResponsibleId: values.ResponsibleId,
				Information: values.Information,
			};
			await mutationOrFn(request);

			props.handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			props.handleClose();
			AlertRequest.failed();
		}
	};

	const selectedDateAsLocal = (() => {
		if (typeof props.selectedDate === "string") {
			return toDateTimeLocalValue(props.selectedDate, timezone);
		}
		if (props.selectedDate instanceof Date) {
			return toDateTimeLocalValue(props.selectedDate.toISOString(), timezone);
		}
		return null;
	})();
	// Keep an initial drill id if none provided
	useEffect(() => {
		if (selectedDrillId == null && drills.length > 0) {
			setSelectedDrillId(drills[0]?.Id);
		}
	}, [selectedDrillId, drills]);

	// Loading guard
	if (
		isLoadingTrainerOptions ||
		isLoadingDrills ||
		(props.drillId && isLoadingDrill)
	) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	const initialDrillId = selectedDrillId ?? drills[0]?.Id;
	const sectionCardSx = {
		...sharedTaskDialogPanelSx(theme),
		p: { xs: 2, md: 3 },
	};

	return (
		<Formik<NewDrillSessionFormValues>
			initialValues={{
				DrillId: initialDrillId,
				Date:
					selectedDateAsLocal ??
					dayjs().tz(timezone).format("YYYY-MM-DDTHH:mm"),
				ResponsibleId: drill?.Trainer?.Id ?? trainers[0]?.Id ?? 0,
				Description: drill?.Description,
				Information: "",
			}}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ isSubmitting }) => (
				<>
					{/* Watch Formik values at the top level and update selectedDrillId safely */}
					<DrillIdWatcher onChange={(id) => setSelectedDrillId(id)} />

					<Form>
						<Paper variant="outlined" sx={{ ...sectionCardSx, mb: 2 }}>
							<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
								<Translate String="Training.NewDrillSession.Section.Session">
									Session
								</Translate>
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mb: 2 }}
							>
								<Translate String="Training.NewDrillSession.Section.Session.Desc">
									Select drill, date and responsible person.
								</Translate>
							</Typography>
							<Stack spacing={2}>
								{!props.drillId && (
									<CustomSelect
										id="DrillId"
										label="Drill"
										name="DrillId"
										translationString="Training.NewDrillSession.Drill"
										defaultValue={initialDrillId}
										style={{ width: "100%" }}
									>
										{drills.map((x) => (
											<option value={x.Id} key={x.Id}>
												{x.Name}
											</option>
										))}
									</CustomSelect>
								)}
								<CustomInput
									id="date"
									type="datetime-local"
									label="Date"
									name="Date"
									translationString="Training.NewDrillSession.Date"
								/>
								<CustomSelect
									id="responsibleId"
									label="Trainer"
									name="ResponsibleId"
									translationString="Training.NewDrillSession.Responsible"
									style={{ width: "100%" }}
								>
									{trainers.map((x) => (
										<option value={x.Id} key={x.Id}>
											{x.Name}
										</option>
									))}
								</CustomSelect>
							</Stack>
						</Paper>

						<Paper variant="outlined" sx={{ ...sectionCardSx, mb: 2 }}>
							<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
								<Translate String="Training.NewDrillSession.Section.Description">
									Description
								</Translate>
							</Typography>
							<Box
								sx={{
									mt: 1,
									px: 2,
									py: 1.5,
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
									bgcolor: "background.default",
								}}
							>
								{drill?.Description ? (
									<SATOSContentRender
										isNotProcedureRender={true}
										content={drill.Description}
									/>
								) : (
									<Typography variant="body2" component="em">
										No description provided.
									</Typography>
								)}
							</Box>
						</Paper>

						<Paper variant="outlined" sx={sectionCardSx}>
							<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
								<Translate String="Training.NewDrillSession.Section.Notes">
									Notes
								</Translate>
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mb: 2 }}
							>
								<Translate String="Training.NewDrillSession.Section.Notes.Desc">
									Additional information about this session.
								</Translate>
							</Typography>
							<SATOSEditorField
								id="information"
								name="Information"
								label="Information"
								aria-required={true}
								aria-describedby="description-error-text"
								style={{ minWidth: "100%" }}
								imageUploadDisabled={true}
							/>
						</Paper>

						<DialogActions
							sx={{
								...sharedTaskDialogActionsSx(theme, isMobile),
								px: 0,
								pt: 2,
								mt: 3,
								display: "flex",
								justifyContent: "flex-end",
								gap: 1.5,
							}}
						>
							<CustomCloseButton
								handleClose={props.handleClose}
								disabled={isSubmitting}
							/>
							<CustomSaveButton
								loading={isSubmitting}
								disabled={isSubmitting}
							/>
						</DialogActions>
					</Form>
				</>
			)}
		</Formik>
	);
};

/** Watches Formik's DrillId and notifies parent (top-level hook safe). */
const DrillIdWatcher: FC<{ onChange: (id?: number) => void }> = ({
	onChange,
}) => {
	const { values } = useFormikContext<NewDrillSessionFormValues>();

	useEffect(() => {
		if (typeof values?.DrillId === "number") {
			onChange(values.DrillId);
		}
	}, [values?.DrillId, onChange]);

	return null;
};
